import React, { useState, useEffect } from "react";
import {
  Box, Typography, Grid, Card, TableHead, TableRow, TableCell, Paper
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import AppTable from "../../common/app-table";
import ButtonOne from "../../common/normal-button";
import AppDatePicker from "../../common/app-date-picker";
import DashboardBadge from "../../common/dashboard-badges";
import CustomTabs from "../../common/custom-tabs";

import {
  ColorsConstants,
  borderColors,
  dashboardCardBgColor,
  grayFontColor,
  grayPrimaryFontColor,
} from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";

import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PhoneCallbackOutlinedIcon from "@mui/icons-material/PhoneCallbackOutlined";
import PhoneForwardedOutlinedIcon from "@mui/icons-material/PhoneForwardedOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";

import { useAuth } from "../../hooks/AuthProvider";
// 🟢 NEW: Added GET_TENANT_DETAILS to imports
import { DAILY_USAGE_RCS_ANALYTICS_ENDPOINT, GET_TENANT_DETAILS } from "../../config/urls";
import { formatWithTzLabel } from "../../utils/dateUtils";

// ─── Summary Card ─────────────────────────────────────────────────────────────
const SummaryCard = ({ label, value, messages, Icon, iconColor }) => (
  <Card
    elevation={0}
    sx={{
      border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
      borderRadius: "12px",
      p: 2.5,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      height: "100%",
    }}
  >
    <Box>
      <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mb: 1 }}>
        {label}
      </Typography>
      <Typography sx={{ ...typography.cost_text, color: grayPrimaryFontColor }}>
        {value}
      </Typography>
      <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.secondaryBackgroundColor, mt: 0.5 }}>
        {messages} Messages
      </Typography>
    </Box>
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        bgcolor: iconColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: 20, color: ColorsConstants.primaryWhite }} />
    </Box>
  </Card>
);

// ─── Custom merged table header ───────────────────────────────────────────────
const UsageTableHead = () => {
  const thBase = {
    bgcolor: ColorsConstants.primaryBackgroundColor,
    color: ColorsConstants.primaryWhite,
    ...typography.bodyLarge,
    fontWeight: 500,
    whiteSpace: "nowrap",
    padding: "12px 16px",
    borderBottom: "none",
  };
  const divider = { borderRight: `1px solid ${ColorsConstants.primaryBlue90}` };

  return (
    <TableHead>
      <TableRow>
        <TableCell rowSpan={2} sx={{ ...thBase, ...divider }}>Date</TableCell>
        <TableCell rowSpan={2} sx={{ ...thBase, ...divider }}>Currency</TableCell>
        <TableCell colSpan={5} align="center" sx={{ ...thBase, borderBottom: `1px solid ${ColorsConstants.primaryBlue90}`, ...divider }}>
          Messages
        </TableCell>
        <TableCell colSpan={5} align="center" sx={{ ...thBase, borderBottom: `1px solid ${ColorsConstants.primaryBlue90}` }}>
          Charges
        </TableCell>
      </TableRow>
      <TableRow>
        {[...GROUP_LABELS, "Total"].map((l, i) => (
          <TableCell key={`msg-${l}`} sx={{ ...thBase, ...(i === 4 ? divider : {}) }}>{l}</TableCell>
        ))}
        {[...GROUP_LABELS, "Total"].map((l) => (
          <TableCell key={`chg-${l}`} sx={thBase}>{l}</TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

// ─── Canonical billing groups (column names) + legend ─────────────────────────
// Keys match the API: <key>Messages / <key>Charges per row, <key>Total in summary.
const GROUPS = [
  { key: "basic",        label: "BASIC" },
  { key: "single",       label: "SINGLE" },
  { key: "p2a",          label: "P2A" },
  { key: "conversation", label: "CONVERSATION" },
];
const GROUP_LABELS = GROUPS.map((g) => g.label);

// Which payload types are billed under each canonical group, per bot region.
const LEGENDS = {
  INDIA: {
    title: "India",
    rows: [
      ["BASIC",        "Plain text, 160 characters or less",   ["basic_message", "basic_message"]],
      ["SINGLE",       "Long text, over 160 characters",       ["single_message", "single_message"]],
      ["SINGLE",       "Rich content (buttons, cards, images)", ["single_message", "single_message"]],
      ["P2A",          "Message from the user",                ["p2a_message", "p2a_message"]],
      ["CONVERSATION", "Conversation started by the bot",      ["Conversational", "a2p_conversation"]],
      ["CONVERSATION", "Conversation started by the user",     ["Conversational", "p2a_conversation"]],
    ],
  },
  ROW: {
    title: "US",
    rows: [
      ["BASIC",        "Plain text, 160 characters or less",   ["rich_message", "rich_message"], "(1 segment)"],
      ["BASIC",        "Long text, over 160 characters",       ["rich_message", "rich_message"], "(more segments)"],
      ["SINGLE",       "Rich content (buttons, cards, images)", ["rich_media_message", "rich_media_message"]],
      ["P2A",          "Message from the user",                ["p2a_message", "p2a_message"]],
      ["CONVERSATION", "Conversation started by the bot",      ["Conversational", "a2p_conversation"]],
      ["CONVERSATION", "Conversation started by the user",     ["Conversational", "p2a_conversation"]],
    ],
  },
};

const Code = ({ children }) => (
  <Box component="code" sx={{ fontFamily: "monospace", fontSize: "13px", bgcolor: dashboardCardBgColor, px: 0.75, py: 0.25, borderRadius: 1 }}>
    {children}
  </Box>
);

const legendColumns = [
  { id: "canonical", label: "Canonical", render: (r) => r[0] },
  { id: "kind",      label: "Message kind", render: (r) => r[1] },
  {
    id: "payload",
    label: "Payload category / type",
    render: (r) => (
      <>
        <Code>{r[2][0]}</Code> / <Code>{r[2][1]}</Code>{r[3] ? ` ${r[3]}` : ""}
      </>
    ),
  },
];

const BillingLegend = ({ regions }) =>
  regions.filter((region) => LEGENDS[region]).map((region) => (
    <Box key={region} sx={{ mt: 3 }}>
      <Typography sx={{ ...typography.bodyLarge, color: grayPrimaryFontColor, fontWeight: 500, mb: 1 }}>
        {LEGENDS[region].title}
      </Typography>
      <AppTable
        columns={legendColumns}
        rows={LEGENDS[region].rows}
        rowKey={(row, index) => `${region}-${index}`}
        maxHeight="none"
        autoAdjustHeight={false}
      />
    </Box>
  ));

const EMPTY_SUMMARY = { totalAmount: 0, basicTotal: 0, singleTotal: 0, p2aTotal: 0, conversationTotal: 0, unpricedCount: 0 };

// ─── Main Component ───────────────────────────────────────────────────────────
const RCSUsageDashboard = ({ selectedMonth, onDataLoad }) => {
  const { getValidToken } = useAuth();
  
  const [summary,     setSummary]     = useState(null);
  const [rows,        setRows]        = useState([]);
  const [lastSynced,  setLastSynced]  = useState("");
  const [total,       setTotal]       = useState(0);
  const [loading,     setLoading]     = useState(true);
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [botRegions,  setBotRegions]  = useState([]);

  // 🟢 NEW: State to hold the dynamic billing currency
  const [billingCurrency, setBillingCurrency] = useState("USD");

  // 🟢 NEW: Functions to format currency dynamically
  const getCurrencySymbol = (currencyCode) => {
    switch (currencyCode?.toUpperCase()) {
      case "USD": return "$";
      case "EUR": return "€";
      case "GBP": return "£";
      case "INR": return "₹";
      case "AED": return "د.إ";
      default: return "$"; // Defaulting to $ if none match
    }
  };

  const currencySymbol = getCurrencySymbol(billingCurrency);
  // Summary cards show 2 decimals; table charges show 4 (US per-message rates are fractions of a cent)
  const fmt = (v) => `${currencySymbol}${Number(v || 0).toFixed(2)}`;
  const fmt4 = (v) => `${currencySymbol}${Number(v || 0).toFixed(4)}`;

  // 🟢 NEW: Fetch Tenant Details on Mount
  const fetchTenantDetails = async () => {
    try {
      const tenant = localStorage.getItem("tenant");
      const response = await fetch(GET_TENANT_DETAILS, {
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-ID": tenant,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch tenant details");

      const data = await response.json();
      
      // Set billing currency if it exists in the tenant profile
      if (data.results && data.results.length > 0 && data.results[0].billing_currency) {
        setBillingCurrency(data.results[0].billing_currency);
      }
    } catch (error) {
      console.error("Error fetching tenant details for RCS usage:", error);
    }
  };

  useEffect(() => {
    fetchTenantDetails();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      const monthStr = dayjs(selectedMonth).format("YYYY-MM");

      const response = await fetch(`${DAILY_USAGE_RCS_ANALYTICS_ENDPOINT}?month=${monthStr}&channel=rcs`, {
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch RCS usage data");

      const jsonData = await response.json();
      
      setSummary(jsonData.summary || EMPTY_SUMMARY);
      if (jsonData.currency) setBillingCurrency(jsonData.currency);
      setBotRegions(Array.isArray(jsonData.bot_regions) ? jsonData.bot_regions : []);
      
      const usageRows = Array.isArray(jsonData.usage) ? jsonData.usage : [];
      setRows(usageRows);
      
      if (onDataLoad) onDataLoad(usageRows);
      
      if (jsonData.last_sync) {
        setLastSynced(formatWithTzLabel(jsonData.last_sync, "DD MMMM YYYY, hh:mm A"));
      } else {
        setLastSynced("");
      }
      
      setTotal(usageRows.length);

    } catch (error) {
      console.error("Error fetching RCS usage:", error.message);
      setSummary(EMPTY_SUMMARY);
      setBotRegions([]);
      setRows([]);
      setTotal(0);
      
      if (onDataLoad) onDataLoad([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    if (selectedMonth) {
      load(); 
    }
  }, [selectedMonth]);

  const sumRows = (field) => rows.reduce((a, r) => a + (r[field] || 0), 0);
  const cards = summary ? [
    { label: "Total Amount", value: fmt(summary.totalAmount),       messages: sumRows("totalMessages"),        Icon: AccountBalanceWalletOutlinedIcon, iconColor: ColorsConstants.secondaryBackgroundColor },
    { label: "BASIC",        value: fmt(summary.basicTotal),        messages: sumRows("basicMessages"),        Icon: SmsOutlinedIcon,            iconColor: "#ef4444" },
    { label: "SINGLE",       value: fmt(summary.singleTotal),       messages: sumRows("singleMessages"),       Icon: PhoneForwardedOutlinedIcon, iconColor: "#27a844" },
    { label: "P2A",          value: fmt(summary.p2aTotal),          messages: sumRows("p2aMessages"),          Icon: PhoneCallbackOutlinedIcon,  iconColor: "#8b5cf6" },
    { label: "CONVERSATION", value: fmt(summary.conversationTotal), messages: sumRows("conversationMessages"), Icon: ForumOutlinedIcon,          iconColor: "#f59e0b" },
  ] : [];

  const columns = [
    { id: "date",     label: "Date" },
    { id: "currency", label: "Currency", render: (row) => row.currency || billingCurrency },
    ...GROUPS.map((g) => ({ id: `${g.key}Messages`, label: g.label, render: (row) => row[`${g.key}Messages`] || 0 })),
    { id: "totalMessages", label: "Total", render: (row) => row.totalMessages || 0 },
    ...GROUPS.map((g) => ({ id: `${g.key}Charges`, label: g.label, render: (row) => fmt4(row[`${g.key}Charges`]) })),
    { id: "totalCharges", label: "Total", render: (row) => fmt4(row.totalCharges) },
  ];

  const paginated = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={card.label}>
            <SummaryCard {...card} />
          </Grid>
        ))}
      </Grid>

      <Paper elevation={0} sx={{ border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography sx={{ ...typography.h1, color: grayPrimaryFontColor, fontWeight: 500 }}>
              Monthly Usage
            </Typography>
            {lastSynced && (
              <Box sx={{ color: ColorsConstants.secondaryBackgroundColor, px: 1.5, py: 0.5, borderRadius: 1, fontSize: "14px", fontWeight: 500 }}>
                Last synced on: {lastSynced}
              </Box>
            )}
          </Box>

          <AppTable
            columns={columns}
            rows={paginated}
            rowKey={(row, index) => row.id || index}
            loading={loading}
            emptyMessage="No usage data found for the selected period."
            maxHeight={500}
            customHead={<UsageTableHead />}
            customHeadColSpan={12} // UPDATED: Colspan from 10 to 12
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
          />

          {summary?.unpricedCount > 0 && (
            <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.error, mt: 1.5 }}>
              {summary.unpricedCount} billing event{summary.unpricedCount === 1 ? "" : "s"} this month could not be priced (no matching rate) and are not included in the charges.
            </Typography>
          )}

          <BillingLegend regions={botRegions} />
        </Box>
      </Paper>
    </Box>
  );
};

export default RCSUsageDashboard;